import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  History, ArrowLeft, FileText, TrendingUp, TrendingDown, HandCoins,
  Calendar, Loader2, User as UserIcon,
} from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EXTRA_TONE = {
  extra:    { icon: TrendingUp,   color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200",     label: "Extra" },
  vale:     { icon: HandCoins,    color: "text-amber-600",   bg: "bg-amber-50 border-amber-200",         label: "Vale" },
  desconto: { icon: TrendingDown, color: "text-rose-600",    bg: "bg-rose-50 border-rose-200",           label: "Desconto" },
};

export default function PayrollHistory() {
  const [params, setParams] = useSearchParams();
  const employeeId = params.get("employee_id") || "";
  const [employees, setEmployees] = useState([]);
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [emp, cls] = await Promise.all([
        api.get("/employees"),
        api.get(`/payroll/closings${employeeId ? `?employee_id=${employeeId}` : ""}`),
      ]);
      setEmployees(emp.data || []);
      setClosings(cls.data || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [employeeId]); // eslint-disable-line

  const totalPaid = useMemo(() => {
    if (!closings) return 0;
    if (employeeId) return closings.reduce((s, c) => s + (c.employee?.net || 0), 0);
    return closings.reduce((s, c) => s + (c.total || 0), 0);
  }, [closings, employeeId]);

  const changeEmployee = (v) => {
    const q = new URLSearchParams(params);
    if (!v || v === "all") q.delete("employee_id");
    else q.set("employee_id", v);
    setParams(q);
    setExpanded(null);
  };

  return (
    <div className="space-y-5 pb-20">
      <PageHeader
        icon={History}
        title="Histórico de pagamentos"
        subtitle="Fechamentos passados por funcionário"
        iconColor="bg-slate-700"
        right={
          <Link
            to="/funcionarios/diarias"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#7c3aed] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar à folha
          </Link>
        }
      />

      {/* Filter */}
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 space-y-3">
        <div>
          <label className="text-[11px] font-semibold text-slate-600 uppercase">Funcionário</label>
          <Select value={employeeId || "all"} onValueChange={changeEmployee}>
            <SelectTrigger data-testid="hist-emp-select"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os funcionários</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] text-slate-500">
            {closings.length} fechamento{closings.length === 1 ? "" : "s"}
          </p>
          <p className="font-mono font-bold text-[#7c3aed]" data-testid="hist-total">{BRL(totalPaid)}</p>
        </div>
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#7c3aed]" /></div>}

      {!loading && closings.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
          <History className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Nenhum fechamento registrado ainda.</p>
        </div>
      )}

      {!loading && closings.map((c) => {
        const isOpen = expanded === c.id;
        return (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-100 card-shadow overflow-hidden" data-testid={`hist-row-${c.id}`}>
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : c.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="text-left min-w-0">
                <p className="font-heading font-black text-[#1e3a8a] leading-tight">
                  {formatDateBR(c.start)} → {formatDateBR(c.end)}
                </p>
                <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Fechado em {new Date(c.closed_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading text-lg font-black text-[#7c3aed] leading-none">
                  {BRL(employeeId ? c.employee?.net : c.total)}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {employeeId
                    ? `${c.employee?.present}P/${c.employee?.half}½/${c.employee?.absent}F`
                    : `${c.rows?.length || 0} funcionário(s)`}
                </p>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3 space-y-2">
                {(employeeId ? [c.employee] : c.rows).map((r) => (
                  <div key={r.employee_id} className="bg-white rounded-xl border border-slate-100 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate inline-flex items-center gap-1.5">
                          <UserIcon className="w-3.5 h-3.5 text-slate-400" /> {r.name}
                        </p>
                        <p className="text-[10px] text-slate-500">{r.role || "—"} · {BRL(r.daily_rate)}/dia</p>
                      </div>
                      <p className="font-bold text-[#7c3aed]">{BRL(r.net)}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-[10px] text-center mb-2">
                      <div><p className="font-bold text-slate-700">{r.present}</p><p className="text-slate-500">Presente</p></div>
                      <div><p className="font-bold text-slate-700">{r.half}</p><p className="text-slate-500">Meia</p></div>
                      <div><p className="font-bold text-slate-700">{r.absent}</p><p className="text-slate-500">Falta</p></div>
                      <div><p className="font-bold text-slate-700">{r.folga}</p><p className="text-slate-500">Folga</p></div>
                    </div>
                    <div className="text-[11px] text-slate-700 space-y-0.5">
                      <div className="flex justify-between"><span>Bruto (diárias)</span><span className="font-mono">{BRL(r.gross)}</span></div>
                      {r.extras_total > 0 && <div className="flex justify-between text-emerald-700"><span>+ Extras</span><span className="font-mono">{BRL(r.extras_total)}</span></div>}
                      {r.vales_total > 0 && <div className="flex justify-between text-amber-700"><span>− Vales</span><span className="font-mono">{BRL(r.vales_total)}</span></div>}
                      {r.descontos_total > 0 && <div className="flex justify-between text-rose-700"><span>− Descontos</span><span className="font-mono">{BRL(r.descontos_total)}</span></div>}
                      <div className="flex justify-between pt-1 mt-1 border-t border-slate-100 font-bold">
                        <span>Líquido pago</span><span className="font-mono text-[#7c3aed]">{BRL(r.net)}</span>
                      </div>
                    </div>
                    {r.extras_items?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {r.extras_items.map((x) => {
                          const tone = EXTRA_TONE[x.type];
                          return (
                            <div key={x.id} className={`flex items-center gap-2 px-2 py-1 rounded border ${tone.bg}`}>
                              <tone.icon className={`w-3 h-3 ${tone.color}`} />
                              <span className="text-[10px] font-semibold text-slate-800">
                                {tone.label} · {BRL(x.amount)}
                              </span>
                              <span className="text-[10px] text-slate-500 ml-auto">{formatDateBR(x.date)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
